import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { useDispatch, useSelector } from 'react-redux';
import strings from 'localization/strings';
import { makeStyles } from 'styles/util';
import { createTenantBooking } from 'actions/tenantBookings';
import { fetchActorHasCreditReports } from 'actions/actors';
import { calculateArea } from 'helpers/StorageAreaCalculator';
import { googleTagManagerConstants, createGoogleTagManagerUserAction } from 'integration/google-tag-manager/googleTagManagerHelper';
import { handleResponse } from 'actions/actionHelpers';
import { required } from 'form/validation';
import { sanitizeRegistrationNumber } from 'helpers/StringHelper';
import { createBookingPriceInfo, createStorageBookingItem, createDiscountBookingItem } from 'helpers/PriceHelper';
import { getOwnerExpectedReplyTimeItems } from 'helpers/ActorHelper';
import paymentRecurrences from 'enums/paymentRecurrences';
import organizationTypes from 'enums/organizationTypes';
import bookingItemTypes from 'enums/bookingItemTypes';
import goodsTypes from 'enums/goodsTypes';
import { formatIsoDate } from 'helpers/DateHelper';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { enrichStorages, askForTenantOrganizationType, calculateMinimumBookingLengthForSubscriptionBooking } from 'logic/bookingLogic';
import storageGroupCategories from 'enums/storageGroupCategories';
import additionalServiceStatuses from 'enums/additionalServiceStatuses';
import { getPriceAdjustmentExplanation, getActiveDiscount } from 'helpers/StorageSiteHelper';
import { fetchOpeningTimeSpansForStorageSite } from 'actions/viewStorageSites';
import { add, max, startOfDay } from 'date-fns';
import { parseOpeningTimeSpan, formatDateAndTimeSpan, stripDateCandidates } from 'helpers/OpeningTimeSpanHelper';
import { createBookingItemDataObject } from 'helpers/AdditionalServiceHelper';
import arrayMutators from 'final-form-arrays';
import { getAdditionalServiceConfiguration } from 'helpers/AdditionalServiceHelper';
import clsx from 'clsx';

import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import LoadingWrapper from 'form/LoadingWrapper';
import Form from 'form/Form';
import { TextField, Checkboxes, Radios, showErrorOnBlur } from 'mui-rff';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import MenuItem from '@material-ui/core/MenuItem';
import DateCandidateSelector from 'components/DateCandidateSelector';
import ActorBlocker from 'account/ActorBlocker';
import ShortDate from 'common/ShortDate';
import Length from 'common/Length';
import Area from 'common/Area';
import Percentage from 'common/Percentage';
import InfoIcon from '@material-ui/icons/Info';
import AdditionalServicesSelector from './AdditionalServicesSelector';
import PriceSpecificationList from 'common/PriceSpecification/PriceSpecificationList';

const useStyles = makeStyles(({ theme }) => ({
    dialog: {
        '& .MuiDialog-paper': {
            margin: 0
        }
    },
    section: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        '&:first-child': {
            marginTop: 0
        },
        '&:last-child': {
            marginBottom: 0
        }
    },
    serviceDescription: {
        fontStyle: 'italic'
    },
    detailsList: {
        '& .MuiListItemAvatar-root': {
            textAlign: 'center',
            minWidth: '32px'
        },
        '& .icon': {
            zoom: 1.25
        },
        '& > *': {
            lineHeight: '1.5rem'
        }
    },
    listIcon: {
        marginTop: '5px',
        alignSelf: 'flex-start'
    },
    horizontalClip: {
        overflowX: 'clip'
    }
}));

const BookingRequestDialog = ({ open, onClose, onBookingSuccess, storageSite, bookingRequestData }) => {
    const classes = useStyles();
    const isMobile = !useMediaQuery(theme => theme.breakpoints.up('md'));
    const { appContext } = useAppContext();
    const dispatch = useDispatch();
    const authenticationContext = useSelector(state => state.authentication.context);
    const { user, selectedActor } = authenticationContext;
    const { requestedStartDate, requestedEndDate, length, width, storageGroup } = bookingRequestData;
    const areaCalculation = calculateArea(length, width, storageGroupCategories[storageGroup.category]);
    const [initialValues, setInitialValues] = useState(undefined);
    const [tenantActorHasCreditReports, setTenantActorHasCreditReports] = useState(undefined);

    const additionalServices = (storageSite.additionalServices || [])
        .filter(additionalService => additionalService.status !== additionalServiceStatuses.deleted.key);

    const showTenantOrganizationTypeRadios = askForTenantOrganizationType(storageSite, storageGroup);

    useEffect(() => {
        if(open) {
            setInitialValues({
                description: undefined,
                goodsType: undefined,
                registrationNumber: undefined,
                noRegistrationNumber: false,
                additionalServices: additionalServices.map(additionalService => {
                    const configuration = getAdditionalServiceConfiguration(additionalService, storageSite);
                    return {
                        ...additionalService,
                        configuration,
                        isPrimaryRow: true,
                        selected: false,
                        quantity: 0,
                        data: undefined
                    };
                }),
                requestedService: undefined,
                selectedStartDate: undefined,
                selecedEndDate: undefined,
                storageId: storageGroup.enumerateStorages && storageGroup.quantity === 1
                    ? storageGroup.storages[0].id // if there is only one storage, don't show dropdown for selection but auto-assign instead
                    : -1, // to properly select "assigned by owner",
                isImmediateBooking: false,
                tenantOrganizationType: showTenantOrganizationTypeRadios
                    ? undefined
                    : selectedActor?.organizationType ?? organizationTypes.private.key,
            });

            // determine the dates that are available for check-in and optionally check-out
            const startDateFrom = max([startOfDay(new Date()), add(requestedStartDate, { months: -3 })]);
            const startDateTo = max([startOfDay(new Date()), add(requestedStartDate, { months: 3 })]);
            let count = 1;
            dispatch(fetchOpeningTimeSpansForStorageSite(storageSite.id, formatIsoDate(startDateFrom, appContext), formatIsoDate(startDateTo, appContext)))
                .then(handleResponse(
                    response => {
                        setStartDateCandidates(stripDateCandidates(response.payload.map(parseOpeningTimeSpan), requestedStartDate));
                        setLoadingCount(loadingCount - 1);
                    },
                    () => {
                        setLoadingCount(loadingCount - 1);
                    }
                ));
            if(requestedEndDate) {
                const endDateFrom = add(requestedEndDate, { months: -3 });
                const endDateTo = add(requestedEndDate, { months: 3 });
                dispatch(fetchOpeningTimeSpansForStorageSite(storageSite.id, formatIsoDate(endDateFrom, appContext), formatIsoDate(endDateTo, appContext)))
                    .then(handleResponse(
                        response => {
                            setEndDateCandidates(stripDateCandidates(response.payload.map(parseOpeningTimeSpan), requestedEndDate));
                            setLoadingCount(loadingCount - 1);
                        },
                        () => {
                            setLoadingCount(loadingCount - 1);
                        }
                    ));
                count++;
            }
            if(appContext.creditReport.enabled) {
                dispatch(fetchActorHasCreditReports(selectedActor.id))
                    .then(handleResponse(
                        response => {
                            setTenantActorHasCreditReports(response.payload.hasCreditReports);
                            setLoadingCount(loadingCount - 1);
                        },
                        () => {
                            setLoadingCount(loadingCount - 1);
                        }
                    ));
                count++;
            }

            setLoadingCount(count);
        }
    }, [open]);

    const subscriptionBooking = !storageSite.endDate && !requestedEndDate;

    const goodsTypeSelectListItems = [{ title: '' }].concat(Object.values(goodsTypes));

    const bookableStorages = storageGroup.enumerateStorages && storageGroup.quantity > 1
        ? [{ id: -1, title: strings.assignedByOwner }].concat(enrichStorages(storageGroup, undefined, requestedStartDate, requestedEndDate).filter(storage => storage.isBookable))
        : undefined;

    const currency = storageGroup.currency;

    const [startDateCandidates, setStartDateCandidates] = useState(undefined);
    const [endDateCandidates, setEndDateCandidates] = useState(undefined);
    const [loadingCount, setLoadingCount] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    const canUseRequestedStartDate = startDateCandidates && !!startDateCandidates.find(o => formatIsoDate(o.date, appContext) === formatIsoDate(requestedStartDate, appContext));
    const canUseRequestedEndDate = endDateCandidates && !!endDateCandidates.find(o => formatIsoDate(o.date, appContext) === formatIsoDate(requestedEndDate, appContext));
    const showStartDateSelector = startDateCandidates && !canUseRequestedStartDate;
    const showEndDateSelector = endDateCandidates && requestedEndDate && !canUseRequestedEndDate;

    const handleFormSubmit = formValues => {
        setIsSaving(true);
        const createBookingRequest = {
            tenantActorId: selectedActor.id,
            tenantUserId: user.id,
            description: formValues.description,
            goodsType: formValues.goodsType,
            registrationNumber: !formValues.noRegistrationNumber
                ? sanitizeRegistrationNumber(formValues.registrationNumber)
                : undefined,
            startDate: formatIsoDate(getStartDate(formValues), appContext),
            endDate: formatIsoDate(getEndDate(formValues), appContext),
            bookingItems: getBookingItemRequests(formValues),
            area: areaCalculation.adjustedArea,
            width: areaCalculation.adjustedWidth,
            length: areaCalculation.adjustedLength,
            storageGroupId: storageGroup.id,
            storageId: formValues.storageId === -1
                ? undefined
                : formValues.storageId,
            isImmediateBooking: formValues.isImmediateBooking,
            tenantOrganizationType: formValues.tenantOrganizationType ?? selectedActor?.organizationType
        };

        const googleTagManagerMetadata = `${storageGroup.category}-${(requestedEndDate ? 'period' : 'subscription')}`;
        dispatch(createTenantBooking(createBookingRequest))
            .then(handleResponse(
                response => {
                    setIsSaving(false);
                    if(!createBookingRequest.isImmediateBooking) {
                        dispatch(createGoogleTagManagerUserAction(googleTagManagerConstants.userActions.sendBookingRequest, googleTagManagerMetadata));
                    }
                    onBookingSuccess(response.payload);
                },
                () => {
                    setIsSaving(false);
                    return { dialog: { title: strings.bookingRequestFailed } };
                }
            ));
    };

    const getFormattedArea = () => {
        const area = areaCalculation.adjustedArea
            ? areaCalculation.adjustedLength * areaCalculation.adjustedWidth
            : storageGroup.storageArea;
        return <Area value={area} maxNumberOfDecimals={1} />;
    };

    const getFormattedLength = () => {
        return <Length value={areaCalculation.adjustedArea ? areaCalculation.adjustedLength : storageGroup.storageLength} maxNumberOfDecimals={1}/>;
    };

    const getFormattedWidth = () => {
        return <Length value={areaCalculation.adjustedArea ? areaCalculation.adjustedWidth : storageGroup.storageWidth} maxNumberOfDecimals={1}/>;
    };

    const getFormattedCeilingHeight = () => <Length value={storageGroup.ceilingHeight} maxNumberOfDecimals={1}/>;

    const getStartDate = values => showStartDateSelector
        ? values.selectedStartDate
        : requestedStartDate;

    const getEndDate = values => showEndDateSelector
        ? values.selectedEndDate
        : requestedEndDate;

    const getCheckInOpeningTimeSpan = values => {
        const startDate = getStartDate(values);
        if(!startDateCandidates) {
            return undefined;
        }
        return startDateCandidates.find(o => formatIsoDate(o.date, appContext) === formatIsoDate(startDate, appContext));
    };

    const getCheckOutOpeningTimeSpan = values => {
        const endDate = getEndDate(values);
        if(!endDate || !endDateCandidates) {
            return undefined;
        }
        return endDateCandidates.find(o => formatIsoDate(o.date, appContext) === formatIsoDate(endDate, appContext));
    };

    const getBookingItemRequests = values => {
        const result = values.additionalServices
            .filter(additionalService => additionalService.selected)
            .map(additionalService => ({
                referenceId: additionalService.id,
                type: bookingItemTypes.service.key,
                data: createBookingItemDataObject(additionalServices.find(o => o.id === additionalService.id), storageSite, additionalService)
            }));
        if(values.requestedService) {
            result.push({
                description: values.requestedService,
                paymentRecurrence: paymentRecurrences.oneTime.key,
                type: bookingItemTypes.requestedService.key
            });
        }
        return result;
    };

    const getBookingItems = values => {
        const booking = {
            startDate: getStartDate(values),
            endDate: getEndDate(values),
            area: areaCalculation.adjustedArea,
            tenantOrganizationType: values.tenantOrganizationType
        };
        const storage = storageGroup.storages.find(o => o.id === values.storageId);
        const storageBookingItem = createStorageBookingItem(booking, storageSite, storageGroup, storage, appContext);
        const discountBookingItem = createDiscountBookingItem(storageSite, storageGroup, storageBookingItem, booking, appContext);
        return [storageBookingItem, discountBookingItem]
                .concat(getBookingItemRequests(values))
                .filter(o => o)
                .map(bir => {
                    if(bir.type === bookingItemTypes.service.key) {
                        const additionalService = storageSite.additionalServices.find(o => o.id === bir.referenceId);
                        if(additionalService) {
                            return {
                                type: bookingItemTypes.service.key,
                                paymentRecurrence: additionalService.paymentRecurrence,
                                description: additionalService.description,
                                timePeriodPrices: additionalService.timePeriodPrices,
                                currency
                            };
                        }
                    }
                    return { ...bir };
                });
    };

    const tenantOrganizationTypeItems = Object.values(organizationTypes).map(o => ({
        label: o.bookingAs,
        value: o.key
    }));
    const additionalServicesAvailable = additionalServices.length > 0;
    const isVehicle = storageGroup.category === storageGroupCategories.vehicle.key;
    const showDescription = !isVehicle;
    const showGoodsType = isVehicle;
    const showRegistrationNumber = isVehicle;
    const describeItemString = isVehicle
        ? strings.vehicleDescribeItem
        : strings.describeItem;
    const describeItemPlaceholderString = isVehicle
        ? strings.vehicleDescribeItemPlaceholder
        : undefined;

    const dialogTitle = storageSite.allowImmediateBookings
        ? strings.sendBooking
        : strings.sendBookingRequest;

    const validateFormValues = formValues => {
        const errors = {};
        if(showDescription && !formValues.description) {
            errors.description = strings.validation.required;
        }
        if(showGoodsType && !formValues.goodsType) {
            errors.goodsType = strings.validation.required;
        }
        if(showRegistrationNumber && !sanitizeRegistrationNumber(formValues.registrationNumber) && !formValues.noRegistrationNumber) {
            errors.registrationNumber = strings.validation.required;
        }
        return errors;
    };

    const allowImmediateBookings = values => storageSite.allowImmediateBookings && !values.requestedService;

    const renderDiscountInformation = booking => {
        const activeDiscount = getActiveDiscount(storageSite, storageGroup, booking, true /* validateEligibility */);
        if(activeDiscount && activeDiscount.numberOfMonths === undefined) {
            return (
                <Box className={classes.section}>
                    {strings.formatString(strings.xDiscountHasBeenDeducted, <Percentage value={activeDiscount.percentage} />)}
                </Box>
            );
        }
        return undefined;
    };

    const renderPriceAdjustmentInformation = () => {
        if(!storageSite.annualPriceAdjustmentDate) {
            return undefined;
        }
        return (
            <Box className={classes.section}>
            {
                getPriceAdjustmentExplanation(
                        storageSite.annualPriceAdjustmentPercentage < 0 ? strings.priceDecreaseDescriptionTemplate : strings.priceIncreaseDescriptionTemplate,
                        Math.abs(storageSite.annualPriceAdjustmentPercentage),
                        storageSite.annualPriceAdjustmentDate,
                        appContext
                    )
                }
            </Box>
        );
    };

    const renderCreditReportInformation = () => {
        if(!appContext.creditReport.enabled) {
            return undefined;
        }
        return (
            <Box className={classes.section}>
                <Typography variant="h5" gutterBottom>
                    {strings.creditCheck}
                </Typography>
                <Typography variant="body1">
                    {tenantActorHasCreditReports ? strings.creditCheckAlreadyPerformedTenantInformation : strings.creditCheckTenantInformation}
                </Typography>
            </Box>
        );
    };

    const renderOwnerExpectedReplyTime = () => {
        const value = getOwnerExpectedReplyTimeItems().find(v => v.value === storageSite.ownerExpectedReplyTime);
        if (!value) {
            return undefined;
        }
        return (
            <Box className={classes.section}>
                <Typography variant="body1">{strings.ownerExpectedReplyTime} {value.text.toLowerCase()}.</Typography>
            </Box>
        );
    };

    const renderDateSelector = (startDate, endDate) => {
        if(loadingCount > 0) {
            return undefined;
        }

        if(canUseRequestedStartDate && (canUseRequestedEndDate || !requestedEndDate)) {
            return undefined;
        }

        const availableStartDateCandidates = startDateCandidates
            ?.filter(candidate => !endDate || candidate.date < endDate);

        const availableEndDateCandidates = endDateCandidates
            ?.filter(candidate => !startDate || candidate.date > startDate);

        return (
            <Box className={classes.section}>
                {
                    showStartDateSelector &&
                    (
                        <>
                            <Typography variant="h5" gutterBottom>
                                {strings.selectCheckInDateTitle}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                {strings.formatString(strings.selectCheckInDateDescription, <ShortDate value={requestedStartDate}/>)}
                            </Typography>
                            <DateCandidateSelector
                                availableDateCandidates={availableStartDateCandidates}
                                name="selectedStartDate"
                                label={strings.startDate}
                            />
                        </>
                    )
                }
                {
                    showEndDateSelector &&
                    (
                        <>
                            <Typography variant="h5" gutterBottom>
                                {strings.selectCheckOutDateTitle}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                {strings.formatString(strings.selectCheckOutDateDescription, <ShortDate value={requestedEndDate}/>)}
                            </Typography>
                            <DateCandidateSelector
                                availableDateCandidates={availableEndDateCandidates}
                                name="selectedEndDate"
                                label={strings.endDate}
                            />
                        </>
                    )
                }
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            className={classes.dialog}
            fullWidth
        >
            <LoadingWrapper
                isSaving={isSaving}
            >
                <Form
                    initialValues={initialValues}
                    mutators={arrayMutators}
                    onSubmit={handleFormSubmit}
                    validate={validateFormValues}
                >
                    {({ handleSubmit, form, values, invalid: invalidFromForm }) => {
                        const startDate = getStartDate(values);
                        const endDate = getEndDate(values);
                        const invalid = invalidFromForm || !startDate || (!subscriptionBooking && !endDate);

                        const checkInOpeningTimeSpan = getCheckInOpeningTimeSpan(values);
                        const checkOutOpeningTimeSpan = getCheckOutOpeningTimeSpan(values);

                        const booking = {
                            subscriptionBooking,
                            startDate,
                            endDate,
                            currency,
                            tenantOrganizationType: selectedActor?.organizationType ?? organizationTypes.private.key
                        };
                        booking.priceInfo = createBookingPriceInfo(booking, getBookingItems(values), undefined, appContext);

                        return (
                            <form onSubmit={handleSubmit}>
                                <DialogTitle disableTypography>
                                    <Typography variant="h5">
                                        {dialogTitle}
                                    </Typography>
                                </DialogTitle>
                                <DialogContent className={classes.horizontalClip}>
                                    <ActorBlocker block={selectedActor?.isBlocked || user?.isBlocked}>
                                        <Box className={classes.section}>
                                            <Typography variant="h5" gutterBottom>
                                                {strings.storage}: {storageSite.title}
                                            </Typography>
                                            {
                                                showDescription &&
                                                (
                                                    <TextField
                                                        name="description"
                                                        label={describeItemString}
                                                        placeholder={describeItemPlaceholderString}
                                                        variant="outlined"
                                                        showError={showErrorOnBlur}
                                                        required
                                                        fieldProps={{ validate: required }}
                                                    />
                                                )
                                            }
                                            {
                                                showGoodsType &&
                                                (
                                                    <TextField
                                                        select
                                                        name="goodsType"
                                                        label={strings.vehicleType}
                                                        variant="outlined"
                                                        required
                                                        fieldProps={{ validate: required }}
                                                    >
                                                        {
                                                            goodsTypeSelectListItems.map((goodsType, i) => (
                                                                <MenuItem key={(goodsType.key ?? i)} value={goodsType.key}>
                                                                    {goodsType.title}
                                                                </MenuItem>
                                                            ))
                                                        }
                                                    </TextField>
                                                )
                                            }
                                            {
                                                showRegistrationNumber &&
                                                (
                                                    <>
                                                        <TextField
                                                            name="registrationNumber"
                                                            label={strings.registrationNumber}
                                                            placeholder={strings.registrationNumberPlaceholder}
                                                            maxLength={16}
                                                            variant="outlined"
                                                        />
                                                        <Checkboxes
                                                            name="noRegistrationNumber"
                                                            data={{ label: strings.noRegistrationNumber, value: true }}
                                                        />
                                                    </>
                                                )
                                            }

                                            {
                                                showTenantOrganizationTypeRadios &&
                                                (
                                                    <Radios
                                                        name="tenantOrganizationType"
                                                        data={tenantOrganizationTypeItems}
                                                        showError={showErrorOnBlur}
                                                        required
                                                        fieldProps={{ validate: required }}
                                                    />
                                                )
                                            }
                                        </Box>

                                        {
                                            additionalServicesAvailable &&
                                            (
                                                <Box className={classes.section}>
                                                    <Typography variant="h5" gutterBottom>
                                                        {strings.additionalServices}
                                                    </Typography>
                                                    <AdditionalServicesSelector
                                                        startDate={startDate}
                                                        fieldNamePrefix="additionalServices"
                                                    />
                                                </Box>
                                            )
                                        }

                                        {
                                            storageSite.serviceDescription &&
                                            (
                                                <Box className={classes.section}>
                                                    <Typography variant="h5" gutterBottom>
                                                        {additionalServicesAvailable ? strings.otherAdditionalServices : strings.additionalServices}
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        {strings.theOwnerOffersAdditionalServices}
                                                    </Typography>
                                                    <Typography className={classes.serviceDescription} variant="body1" gutterBottom>
                                                        {storageSite.serviceDescription}
                                                    </Typography>
                                                    <TextField
                                                        name="requestedService"
                                                        label={strings.additionalServicesToOrder}
                                                        helperText={strings.additionalServicesToOrderHelperText}
                                                        variant="outlined"
                                                        multiline
                                                        rows={2}
                                                    />
                                                </Box>
                                            )
                                        }

                                        {
                                            bookableStorages && bookableStorages.length > 1 &&
                                            (
                                                <Box className={classes.section}>
                                                    <TextField
                                                        select
                                                        name="storageId"
                                                        label={strings.selectStorageLabel}
                                                        helperText={strings.selectStorageHelperText}
                                                        variant="outlined"
                                                    >
                                                        {
                                                            bookableStorages.map((storage, i) => (
                                                                <MenuItem key={(storage.id ?? i)} value={storage.id}>
                                                                    {storage.title}
                                                                </MenuItem>
                                                            ))
                                                        }
                                                    </TextField>
                                                </Box>
                                            )
                                        }

                                        {renderDateSelector(startDate, endDate)}

                                        <List className={clsx(classes.detailsList, classes.section)}>
                                            <ListItem disableGutters>
                                                <ListItemAvatar className={classes.listIcon}>
                                                    <span className="icon icon-location"/>
                                                </ListItemAvatar>
                                                <ListItemText>
                                                    {storageSite.city}
                                                </ListItemText>
                                            </ListItem>

                                            <ListItem disableGutters>
                                                <ListItemAvatar className={classes.listIcon}>
                                                    <span className="icon icon-kvm"/>
                                                </ListItemAvatar>
                                                <ListItemText>
                                                    {strings.area}: {getFormattedArea()}
                                                    {
                                                        (areaCalculation.adjustedArea || (storageGroup.storageLength && storageGroup.storageWidth)) &&
                                                        (
                                                            <>
                                                                <br/>
                                                                {strings.length}: {getFormattedLength()}
                                                                <br/>
                                                                {strings.width}: {getFormattedWidth()}
                                                            </>
                                                        )
                                                    }
                                                    {
                                                        storageGroup.ceilingHeight &&
                                                        (
                                                            <>
                                                                <br/>
                                                                {strings.ceilingHeight}: {getFormattedCeilingHeight()}
                                                            </>
                                                        )
                                                    }
                                                </ListItemText>
                                            </ListItem>

                                            <ListItem disableGutters>
                                                <ListItemAvatar className={classes.listIcon}>
                                                    <span className="icon icon-calendar"/>
                                                </ListItemAvatar>
                                                <ListItemText>
                                                    {
                                                        startDate &&
                                                        (
                                                            <>{strings.checkIn}: {formatDateAndTimeSpan(startDate, checkInOpeningTimeSpan, appContext)}</>
                                                        )
                                                    }
                                                    {
                                                        !startDate &&
                                                        (
                                                            <>{strings.checkIn}: {strings.dateNotSelected}</>
                                                        )
                                                    }
                                                </ListItemText>
                                            </ListItem>

                                            <ListItem disableGutters>
                                                <ListItemAvatar className={classes.listIcon}>
                                                    <span className="icon icon-calendar"/>
                                                </ListItemAvatar>
                                                <ListItemText>
                                                    {
                                                        endDate &&
                                                        (
                                                            <>{strings.checkOut}: {formatDateAndTimeSpan(endDate, checkOutOpeningTimeSpan, appContext)}</>
                                                        )
                                                    }
                                                    {
                                                        !subscriptionBooking && !endDate &&
                                                        (
                                                            <>{strings.checkOut}: {strings.dateNotSelected}</>
                                                        )
                                                    }
                                                    {
                                                        subscriptionBooking && strings.bookingIsUntilFurther
                                                    }
                                                </ListItemText>
                                            </ListItem>

                                            {
                                                endDate &&
                                                (
                                                    <ListItem disableGutters>
                                                        <ListItemAvatar className={classes.listIcon}>
                                                            <InfoIcon/>
                                                        </ListItemAvatar>
                                                        <ListItemText>
                                                            {strings.periodBookingEndDateMessage}
                                                        </ListItemText>
                                                    </ListItem>
                                                )
                                            }

                                            {
                                                subscriptionBooking &&
                                                (
                                                    <>
                                                        <ListItem disableGutters>
                                                            <ListItemAvatar className={classes.listIcon}>
                                                                <span className="icon icon-time"/>
                                                            </ListItemAvatar>
                                                            <ListItemText>
                                                                {strings.minimumBookingLength}: {calculateMinimumBookingLengthForSubscriptionBooking(storageSite)} {strings.daysLower}
                                                            </ListItemText>
                                                        </ListItem>
                                                        <ListItem disableGutters>
                                                            <ListItemAvatar className={classes.listIcon}>
                                                                <span className="icon icon-time"/>
                                                            </ListItemAvatar>
                                                            <ListItemText>
                                                                {strings.noticePeriod}: {strings.noticePeriodDescription}
                                                            </ListItemText>
                                                        </ListItem>
                                                    </>
                                                )
                                            }

                                        </List>
                                        <PriceSpecificationList booking={booking} horizontalMargin={false}/>
                                        { renderDiscountInformation(booking) }
                                        { renderPriceAdjustmentInformation() }
                                        { renderCreditReportInformation() }
                                        { renderOwnerExpectedReplyTime() }
                                    </ActorBlocker>
                                </DialogContent>
                                <DialogActions>
                                    {
                                        allowImmediateBookings(values) &&
                                        (
                                            <Button
                                                type="submit"
                                                color="primary"
                                                variant="contained"
                                                disabled={invalid || values.requestedService || selectedActor?.isBlocked || user?.isBlocked}
                                                fullWidth={isMobile}
                                                onClick={() => form.change('isImmediateBooking', true)}
                                            >
                                                {strings.payImmediately}
                                            </Button>
                                        )
                                    }
                                    {
                                        !allowImmediateBookings(values) &&
                                        (
                                            <Button
                                                type="submit"
                                                color="primary"
                                                variant="contained"
                                                disabled={invalid || selectedActor?.isBlocked || user?.isBlocked}
                                                fullWidth={isMobile}
                                            >
                                                {strings.send}
                                            </Button>
                                        )
                                    }
                                    <Button
                                        color="primary"
                                        variant="outlined"
                                        onClick={onClose}
                                        fullWidth={isMobile}
                                    >
                                        {strings.cancel}
                                    </Button>
                                </DialogActions>
                            </form>
                        );
                    }}
                </Form>
            </LoadingWrapper>
        </Dialog>
    );
};

BookingRequestDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onBookingSuccess: PropTypes.func,
    storageSite: PropTypes.object,
    bookingRequestData: PropTypes.object,
};

export default BookingRequestDialog;
